import { useLayoutEffect, useRef } from 'react';

import { Backdrop, Grow } from '@mui/material';

import { debounce } from '@/utils/functionalUtils';

import { CaptchaContainer } from '@/shared/captcha/CaptchaContainer';
import { CaptchaPolicy } from '@/shared/captcha/CaptchaPolicy';

import type { TUserConsentInfo } from '@/types/profile';

import { ResizeObserver } from '@/compatibility/resizeObserver';

import { ConsentForm } from '../../consentForm/ConsentForm';
import type { TAcceptanceDialogProps } from '../types';

import { useStyles } from './AcceptanceDialog.styles';

export const AcceptanceDialog = ({
  popup,
  loading,
  onClose,
  titleRef,
  captcha: captchaHelper,
}: TAcceptanceDialogProps) => {
  const { captcha, captchaFailed, captchaContainerRef } = captchaHelper;

  const dialog = useRef<HTMLDivElement>(null);
  const classes = useStyles();
  const opened = !!popup;

  const submit = (consent: TUserConsentInfo) => popup?.onContinue(consent, captcha);

  useLayoutEffect(() => {
    // This is an adjustment for the dialog position
    const GUTTER = 16;

    const callback = () => {
      if (popup && dialog.current && titleRef.current) {
        const { top } = popup.anchor.getBoundingClientRect();
        const { offsetTop, offsetHeight, offsetParent } = popup.anchor;
        const { offsetHeight: containerHeight } = offsetParent as HTMLElement;
        const { offsetHeight: dialogHeight } = dialog.current;
        const titleHeight = titleRef.current.offsetHeight;

        // If there's not actually enough space to show the dialog, pin it to the top of the container
        if (titleHeight + offsetTop - GUTTER < dialogHeight) {
          dialog.current.style.top = `-${titleHeight}px`;
          dialog.current.style.bottom = '';
          // Otherwise, pin it to the top of the anchor
        } else if (top > dialogHeight) {
          dialog.current.style.top = '';
          dialog.current.style.bottom = `${containerHeight - offsetTop}px`;
        } else {
          dialog.current.style.top = `${offsetTop + offsetHeight}px`;
          dialog.current.style.bottom = '';
        }
      }
    };

    const resizeObserver = new ResizeObserver(
      debounce(() => {
        requestAnimationFrame(callback);
      }, 50),
    );
    resizeObserver.observe(document.body);

    callback();

    return () => {
      resizeObserver.disconnect();
    };
  }, [popup, titleRef]);

  return (
    <>
      <Backdrop open={opened} className={classes.visibleBackdrop} />
      <Backdrop open={opened} className={classes.invisibleBackdrop} onClick={onClose} invisible />

      <Grow in={opened}>
        <div ref={dialog} className={classes.dialog}>
          <ConsentForm
            submit={submit}
            loading={loading}
            disabled={captchaFailed}
            classes={{ submitSpinner: classes.submitSpinner }}
            captcha={
              captcha ? (
                <CaptchaContainer ref={captchaContainerRef} version={captcha.version} />
              ) : undefined
            }
          />

          {captcha && <CaptchaPolicy className={classes.captchaPolicy} />}
        </div>
      </Grow>
    </>
  );
};
